import { Box, Button, Checkbox, FormControlLabel, Theme, Typography } from "@material-ui/core";
import React, { FC } from "react";
import { ExamSessionForSignupViewModel } from "../../model/ExamSignup/ExamSessionForSignupViewModel";
import { makeStyles } from "@material-ui/core";
import { useIntl } from "react-intl";
import { GetTranslatedValueIntl } from "../../model/Shared/TranslatedValue";
import { Add as AddIcon, Lock as LockIcon } from "@material-ui/icons";
import TranslatedMessage from "../translations/TranslatedMessage";
import { ExamType } from "../../model/Exam";
import Alert from "@material-ui/lab/Alert";
import { Language } from "../../data/constants";
import { routesPaths } from "../../Routes";
import { getKeyFromLanguage } from "../../utils/LanguageHelper";
import { navigateTo } from "../../utils/Navigation";
import { useHistory } from "react-router";

const useStyles = makeStyles((theme: Theme) => ({
  filter: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: `${theme.spacing(2)}px`,
  },
  sessionWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(2)}px`,
  },
  sessionContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#eee",
    borderRadius: theme.shape.borderRadius,
  },
  sessionButton: {
    minWidth: "250px",
    "& .MuiButton-label": {
      alignSelf: "flex-end",
      display: "flex",
      gap: `${theme.spacing(1)}px`,
    },
  },
  icon: {
    width: "16px",
    height: "16px",
  },
  disabled: {
    pointerEvents: "none",
  },
}));

interface LifeExamSessionsProps {
  sessions: ExamSessionForSignupViewModel[];
  language: Language;
}

const LifeExamSessions: FC<LifeExamSessionsProps> = ({ sessions, language }) => {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const [includeFullyBooked, setIncludeFullyBooked] = React.useState(false);

  const waitingListSessions =
    sessions?.filter((session) =>
      session.courses.every((course) =>
        course.modules.every((module) => module.exams.every((exam) => exam.type === ExamType.TermPaper))
      )
    ) ?? [];
  const regularSessions = (sessions?.filter((session) => waitingListSessions.indexOf(session) === -1) ?? []).sort(
    (a, b) => {
      const aDate = new Date(a.freeCancellationEndDate);
      const bDate = new Date(b.freeCancellationEndDate);
      return aDate.getTime() - bDate.getTime();
    }
  );
  const allSessions = [...regularSessions, ...waitingListSessions];
  const languageKey = getKeyFromLanguage(language);

  return (
    <>
      <Box className={classes.filter} p={2} mb={2}>
        <Box>
          {!includeFullyBooked && (
            <Alert severity="info">
              <TranslatedMessage id="lifeExams.messages.infoAlertExamSessions" />
            </Alert>
          )}
        </Box>
        <FormControlLabel
          label={<TranslatedMessage id="lifeExams.label.displayFullyBooked" />}
          control={
            <Checkbox
              checked={includeFullyBooked}
              onChange={(event) => setIncludeFullyBooked(event.target.checked)}
              color="primary"
              inputProps={{ "aria-label": "secondary checkbox" }}
            />
          }
        />
      </Box>
      <Box className={classes.sessionWrapper} p={2} mb={2}>
        {allSessions.map((session) => {
          if (session.isFullyBooked && !includeFullyBooked) {
            return null;
          }
          const isSessionPaid =
            session.courses?.some((course) =>
              course.modules?.some((module) => module.exams.some((exam) => exam.isPaid))
            ) ?? false;

          return (
            <Box className={classes.sessionContainer} key={session.id} p={2} mb={2}>
              <Typography>{GetTranslatedValueIntl(intl, session.sessionKey)}</Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.sessionButton}
                disabled={session.isFullyBooked || isSessionPaid}
                onClick={() => navigateTo(routesPaths.life(languageKey, session.id), history)}
              >
                {isSessionPaid && <TranslatedMessage id="lifeExams.sessionAlreadyBooked" />}
                {!isSessionPaid && (
                  <>
                    {session.isFullyBooked ? (
                      <>
                        <LockIcon className={classes.icon} />{" "}
                        <TranslatedMessage id="lifeExams.label.sesionFullyBooked" />
                      </>
                    ) : (
                      <>
                        <AddIcon className={classes.icon} /> <TranslatedMessage id="lifeExams.label.register" />
                      </>
                    )}
                  </>
                )}
              </Button>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default LifeExamSessions;
